/**
 * useAddressValidation - USPS-grade address check for manual stops.
 *
 * Runs once per distinct address (cached by React Query), so re-opening the
 * form or toggling fields doesn't re-bill the validation API. Pass `undefined`
 * until the dispatcher has picked an address from the suggestions.
 */

import { useQuery } from '@tanstack/react-query';
import { validateAddress } from '@/lib/supabase/manualStopContacts';
import type { AddressValidationSummary } from '@/lib/validation/manualStopValidation';
import type { AddressValidationRequest, AddressValidationResult } from '@/types/manualStops';

export const addressValidationKeys = {
  all: ['address-validation'] as const,
  detail: (req: AddressValidationRequest) =>
    [
      ...addressValidationKeys.all,
      [req.address_line1, req.address_line2 ?? '', req.city, req.region, req.postal_code ?? '']
        .map(part => part.trim().toUpperCase())
        .join('|')
    ] as const
};

export function useAddressValidation(request: AddressValidationRequest | undefined) {
  return useQuery({
    queryKey: request ? addressValidationKeys.detail(request) : addressValidationKeys.all,
    queryFn: () => validateAddress(request as AddressValidationRequest),
    enabled: !!request,
    staleTime: 30 * 60 * 1000, // an address's USPS status doesn't change within a session
    gcTime: 30 * 60 * 1000,
    retry: false, // validateAddress never throws; 'unavailable' is a valid answer
    refetchOnWindowFocus: false
  });
}

/** Summary stored on the stop (address_validation column). */
export function toValidationSummary(result: AddressValidationResult): AddressValidationSummary {
  return {
    provider: 'google_av',
    status: result.status,
    dpv: result.dpv,
    residential: result.residential,
    business: result.business,
    validated_at: new Date().toISOString(),
    response_id: result.response_id
  };
}
